import { AddIcon } from '@chakra-ui/icons';
import { HStack } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useGetTemplates from './queries/useGetTemplates';
import useDeleteTemplate from './queries/useDeleteTemplate';

import { Button, ConfirmationDialog, Text } from '@/components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';

import { getTemplatesTableColumns } from './templatesTableConfig';

export default function Templates() {
    const gridRef = useRef();
    const navigate = useNavigate();

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const [templateToDelete, setTemplateToDelete] = useState(null);

    const handleEditTemplate = useCallback(
        (templateData) => {
            navigate(`/templates/edit/${templateData.id}`);
        },
        [navigate],
    );

    const handleAddTemplate = () => {
        navigate('/templates/add');
    };

    const handleDeleteTemplate = useCallback((templateData) => {
        setTemplateToDelete(templateData);
    }, []);

    const handleCloseDeleteDialog = () => {
        setTemplateToDelete(null);
    };

    const handleDeleteSuccess = () => {
        toast.success('Template deleted successfully!');
        setTemplateToDelete(null);
    };

    const handleDeleteError = () => {
        toast.error('Failed to delete template. Please try again.');
        setTemplateToDelete(null);
    };

    const { mutate: deleteTemplate, isPending: isDeleting } = useDeleteTemplate(
        handleDeleteSuccess,
        handleDeleteError,
    );

    const handleConfirmDelete = () => {
        if (templateToDelete) {
            deleteTemplate(templateToDelete.id);
        }
    };

    const baseColumns = useMemo(
        () =>
            getTemplatesTableColumns({
                onEditTemplate: handleEditTemplate,
                onDeleteTemplate: handleDeleteTemplate,
            }),
        [handleEditTemplate, handleDeleteTemplate],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching } = useGetTemplates(
        setTableData,
        gridRef,
        baseColumns,
    );

    const isFetchingData = isRefetching || isFetching;

    const titleName = 'Templates';

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    Templates
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                titleName={titleName}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            leftIcon={<AddIcon />}
                            onClick={handleAddTemplate}
                            size="sm"
                            padding="6px 12px">
                            Add Template
                        </Button>
                    </HStack>
                }
            />

            <ConfirmationDialog
                isOpen={!!templateToDelete}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDelete}
                title="Delete Template"
                message={`Are you sure you want to delete "${templateToDelete?.name || 'this template'}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmColorScheme="red"
                isLoading={isDeleting}
            />
        </>
    );
}
